import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, Sparkles, Loader2, TrendingUp,
  ArrowUpRight, ArrowDownRight, Clock, Activity,
  Crown, Medal,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { authFetch, API } from "../utils/api";

/* ── Design tokens ────────────────────────────────────────────────────────── */
const FILTER_OPTS = [
  { label: "Todos",   value: 0  },
  { label: "7 Días",  value: 7  },
  { label: "14 Días", value: 14 },
  { label: "30 Días", value: 30 },
];

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const DONUT_COLORS = ["#FFC107", "#3199BA", "#ff7351", "#34D399", "#A78BFA"];

/* ── Main page ────────────────────────────────────────────────────────────── */
export default function HomeDashboard() {
  const navigate = useNavigate();

  const [days,           setDays]           = useState(0);
  const [data,           setData]           = useState(null);
  const [heatmap,        setHeatmap]        = useState([]);
  const [insight,        setInsight]        = useState(null);
  const [insightLoading, setInsightLoading] = useState(true);
  const [syncing,        setSyncing]        = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [leaderboard,    setLeaderboard]    = useState([]);
  const [financeSummary, setFinanceSummary] = useState([]);
  const [activity,       setActivity]       = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const dayParam = days > 0 ? days : 9999;
      const [aRes, hRes, lRes, fRes, actRes] = await Promise.all([
        authFetch(`${API}/analytics/dashboard?days=${dayParam}`),
        authFetch(`${API}/analytics/email-opens`),
        authFetch(`${API}/analytics/leaderboard`),
        authFetch(`${API}/admin/finance/summary`),
        authFetch(`${API}/admin/activity-log?days=${days > 0 ? days : 30}&limit=8`),
      ]);
      if (aRes?.ok) setData(await aRes.json());
      if (hRes?.ok) setHeatmap(await hRes.json());
      if (lRes?.ok) { const lb = await lRes.json(); setLeaderboard(Array.isArray(lb) ? lb : []); }
      if (fRes?.ok) { const fd = await fRes.json(); setFinanceSummary(fd.by_currency || []); }
      if (actRes?.ok) {
        const a = await actRes.json();
        // Iter93 — endpoint nuevo devuelve {items, total, by_type}; fallback a array legacy
        const list = Array.isArray(a) ? a : (a.items || []);
        setActivity(list);
      }
    } catch (err) { console.error("[HomeDashboard]", err); }
    setLoading(false);
  }, [days]);

  const loadInsight = useCallback(async () => {
    setInsightLoading(true);
    try {
      const res = await authFetch(`${API}/insights/daily`);
      if (res?.ok) setInsight(await res.json());
    } catch (err) { console.warn("[home] insights/daily fetch failed:", err?.message); }
    setInsightLoading(false);
  }, []);

  useEffect(() => { loadData();    }, [loadData]);
  useEffect(() => { loadInsight(); }, [loadInsight]);

  // Auto-refresh metrics every 60 seconds (but NOT the daily insight — that
  // refreshes every 24h server-side and doesn't need to flicker in the UI).
  // Pauses when the tab is hidden to avoid wasting quota on background tabs.
  useEffect(() => {
    const tick = () => {
      if (!document.hidden) loadData();
    };
    const handle = setInterval(tick, 60_000);
    const onVis = () => { if (!document.hidden) loadData(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(handle);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loadData]);

  /* Sync (manual) — retained only internally; button removed from UI per UX request */
  const _unusedSync = async () => { setSyncing(true); await Promise.all([loadData(), loadInsight()]); setSyncing(false); };
  void _unusedSync; void syncing;

  /* ── Derived stats ────────────────────────────────────────────────────── */
  const stats         = data || {};
  const total         = stats.total || 0;
  const nuevo         = stats.stages?.new || 0;
  const investigado   = stats.stages?.researched || 0;
  const seguimiento   = stats.stages?.emailed || 0;
  const sitioListo    = stats.stages?.site_generated || 0;
  const pagado        = stats.stages?.paid || 0;
  const ganado        = (stats.stages?.won || 0) + pagado;
  const perdido       = stats.stages?.lost || 0;
  const conversion    = stats.conversion_rate || 0;

  const heatMatrix = {};
  heatmap.forEach(({ hour, day, count }) => { heatMatrix[`${day}-${hour}`] = count; });
  const maxHeat = Math.max(1, ...heatmap.map(h => h.count));

  const pieData = [
    { name: "Nuevos",      value: nuevo,       color: "#FFC107" },
    { name: "Investigados",value: investigado, color: "#3199BA" },
    { name: "Seguimiento", value: seguimiento, color: "#A78BFA" },
    { name: "Sitio Listo", value: sitioListo,  color: "#FF4F00" },
    { name: "Ganados",     value: ganado,      color: "#34D399" },
    { name: "Perdidos",    value: perdido,     color: "#ff7351" },
  ].filter(d => d.value > 0);

  const totalPie = pieData.reduce((s, d) => s + d.value, 0);

  const finPrimary = financeSummary[0] || null;
  const income  = finPrimary?.income  || 0;
  const expense = finPrimary?.expense || 0;
  const margin  = finPrimary?.margin  || 0;
  const maxBar  = Math.max(income, expense, 1);

  return (
    <div className="min-h-screen pb-10" style={{ background: "var(--ds-surface)", fontFamily: "'Inter', sans-serif" }}>

      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <div
        data-testid="dashboard-header"
        className="flex items-center justify-between px-6 py-4"
        style={{ background: "rgba(14,14,14,0.85)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--ds-text-muted)" }}>SitiosYA</p>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Cuadro de Mando
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <AutoRefreshBadge loading={loading} />
        </div>
      </div>

      <div className="px-6 pt-6 space-y-5">

        {/* ── INSIGHT DEL DÍA ─────────────────────────────────────────────── */}
        <div
          data-testid="insight-card"
          className="rounded-2xl p-5 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #1a1400 0%, #131313 60%)", border: "1px solid rgba(255,193,7,0.20)" }}
        >
          {/* Glow spot */}
          <div className="absolute top-0 left-0 w-48 h-48 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,193,7,0.12) 0%, transparent 70%)", transform: "translate(-30%, -30%)" }} />
          <div className="flex items-center gap-2 mb-2 relative z-10">
            <Sparkles size={14} style={{ color: "var(--ds-primary)" }} />
            <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--ds-primary)" }}>
              Insight del Día
            </span>
          </div>
          {insightLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 size={13} className="animate-spin" style={{ color: "var(--ds-text-muted)" }} />
              <p className="text-xs" style={{ color: "var(--ds-text-muted)" }}>Analizando datos...</p>
            </div>
          ) : (
            <p className="text-sm leading-relaxed relative z-10" style={{ color: "var(--ds-text-muted)" }}>
              {insight?.content || "Sin insight disponible por el momento."}
            </p>
          )}
        </div>

        {/* ── Filter tabs ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: "var(--ds-surface-low)" }}>
            {FILTER_OPTS.map(opt => (
              <button
                key={opt.value}
                data-testid={`filter-${opt.label}`}
                onClick={() => setDays(opt.value)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                style={days === opt.value
                  ? { background: "var(--ds-primary)", color: "#0e0e0e" }
                  : { color: "var(--ds-text-muted)" }
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── KPI pills row ───────────────────────────────────────────────── */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none" data-testid="kpi-row">
          <KpiPill testId="kpi-total"       label="Total Leads"  value={total}          delta="+10%" positive />
          <KpiPill testId="kpi-nuevos"      label="Nuevos"       value={nuevo}          />
          <KpiPill testId="kpi-ganados"     label="Ganados"      value={ganado}         delta="+13%" positive />
          <KpiPill testId="kpi-seguimiento" label="Seguimiento"  value={seguimiento}    delta="+2%"  positive />
          <KpiPill testId="kpi-perdidos"    label="Perdidos"     value={perdido}        delta="-5%"  positive={false} />
          <KpiPill testId="kpi-conversion"  label="Conversión"   value={`${conversion}%`} />
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin" size={32} style={{ color: "var(--ds-primary)" }} />
          </div>
        ) : (
          /* ── Main 2-col grid ──────────────────────────────────────────────── */
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">

            {/* ── LEFT COLUMN ─────────────────────────────────────────────── */}
            <div className="space-y-5">

              {/* Heatmap */}
              {heatmap.length > 0 && (
                <SectionCard title="Apertura por Día y Hora" testId="heatmap-card">
                  <div className="overflow-x-auto">
                    <div className="grid gap-1.5" style={{ gridTemplateColumns: `40px repeat(24, minmax(18px,1fr))` }}>
                      <div />
                      {Array.from({ length: 24 }, (_, h) => (
                        <div key={h} className="text-center" style={{ fontSize: 8, color: "var(--ds-text-muted)" }}>
                          {h % 6 === 0 ? `${h}h` : ""}
                        </div>
                      ))}
                      {DAY_LABELS.map((day, di) => (
                        <React.Fragment key={day}>
                          <div className="flex items-center text-right pr-2" style={{ fontSize: 9, color: "var(--ds-text-muted)", fontWeight: 600 }}>{day}</div>
                          {Array.from({ length: 24 }, (_, h) => {
                            const v = heatMatrix[`${di}-${h}`] || 0;
                            const intensity = v / maxHeat;
                            return (
                              <div
                                key={h}
                                title={`${day} ${h}h: ${v}`}
                                className="h-5 rounded-sm transition-colors"
                                style={{ background: v === 0 ? "rgba(255,255,255,0.05)" : `rgba(255,193,7,${0.15 + intensity * 0.85})` }}
                              />
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 justify-end">
                    <span style={{ fontSize: 9, color: "var(--ds-text-muted)" }}>Menos</span>
                    {[0.15, 0.35, 0.55, 0.75, 1.0].map(i => (
                      <div key={i} className="h-3 w-5 rounded-sm" style={{ background: `rgba(255,193,7,${i})` }} />
                    ))}
                    <span style={{ fontSize: 9, color: "var(--ds-text-muted)" }}>Más</span>
                  </div>
                </SectionCard>
              )}

              {/* Finanzas */}
              {finPrimary && (
                <SectionCard title="Finanzas" testId="finanzas-card" action={{ label: "Ver todo →", onClick: () => navigate("/finance") }}>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p style={{ fontSize: 11, color: "var(--ds-text-muted)" }}>Ganancia Neta Actual</p>
                      <p className="text-3xl font-black mt-1" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", color: "var(--ds-primary)" }}>
                        US$ {Number(margin).toLocaleString("es-UY", { minimumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between mb-1" style={{ fontSize: 11 }}>
                          <span style={{ color: "var(--ds-text-muted)" }}>Ingresos</span>
                          <span className="font-bold text-green-400">US$ {Number(income).toLocaleString("es-UY")}</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                          <div className="h-full rounded-full" style={{ width: `${(income / maxBar) * 100}%`, background: "#34D399" }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1" style={{ fontSize: 11 }}>
                          <span style={{ color: "var(--ds-text-muted)" }}>Egresos</span>
                          <span className="font-bold" style={{ color: "var(--ds-error)" }}>US$ {Number(expense).toLocaleString("es-UY")}</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                          <div className="h-full rounded-full" style={{ width: `${(expense / maxBar) * 100}%`, background: "var(--ds-error)" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </SectionCard>
              )}

              {/* Bottom 3-col: Ranking | Etapa donut | Leads/día */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

                {/* Ranking vendedores */}
                <SectionCard title="Ranking Vendedores" testId="ranking-card">
                  {leaderboard.length === 0 ? (
                    <p className="text-xs py-4 text-center" style={{ color: "var(--ds-text-muted)" }}>Sin ventas aún</p>
                  ) : (
                    <div className="space-y-3">
                      {leaderboard.slice(0, 5).map((entry, i) => {
                        const maxCount = leaderboard[0]?.count || 1;
                        const pct = Math.round((entry.count / maxCount) * 100);
                        const medals = [
                          <Crown key="c" size={13} style={{ color: "var(--ds-primary)" }} />,
                          <Medal key="m1" size={13} className="text-gray-300" />,
                          <Medal key="m2" size={13} className="text-amber-600" />,
                        ];
                        return (
                          <div key={entry.user_id} data-testid={`rank-${entry.user_id}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span>{medals[i] || <span className="text-[10px] font-bold" style={{ color: "var(--ds-text-muted)" }}>{i + 1}</span>}</span>
                                <span className="text-xs font-bold text-white truncate max-w-[90px]">{entry.name}</span>
                              </div>
                              <span className="text-xs font-black" style={{ color: "var(--ds-primary)" }}>{pct}%</span>
                            </div>
                            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: i === 0 ? "var(--ds-primary)" : i === 1 ? "#9CA3AF" : "#92400e" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </SectionCard>

                {/* Donut por etapa */}
                <SectionCard title="Leads por Etapa" testId="donut-card">
                  {pieData.length > 0 ? (
                    <div className="flex flex-col items-center">
                      <div className="relative">
                        <PieChart width={110} height={110}>
                          <Pie data={pieData} cx={55} cy={55} innerRadius={30} outerRadius={50} dataKey="value" paddingAngle={3} startAngle={90} endAngle={-270}>
                            {pieData.map((entry) => <Cell key={entry.name} fill={entry.color} strokeWidth={0} />)}
                          </Pie>
                        </PieChart>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <span className="text-xl font-black" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", color: "var(--ds-primary)" }}>{totalPie}</span>
                          <span style={{ fontSize: 8, color: "var(--ds-text-muted)", fontWeight: 700 }}>TOTAL</span>
                        </div>
                      </div>
                      <div className="w-full space-y-1 mt-2">
                        {pieData.map(d => (
                          <div key={d.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: d.color }} />
                              <span style={{ fontSize: 10, color: "var(--ds-text-muted)" }}>{d.name}</span>
                            </div>
                            <span className="text-xs font-bold text-white">{d.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-center py-4" style={{ color: "var(--ds-text-muted)" }}>Sin datos</p>
                  )}
                </SectionCard>

                {/* Bar chart leads/día */}
                <SectionCard title="Leads por Día" testId="barchart-card">
                  {data?.daily?.some(d => d.count > 0) ? (
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={(data.daily || []).slice(-14)} barSize={7} margin={{ left: -28, right: 2, top: 4 }}>
                        <XAxis dataKey="date" tickFormatter={d => { const [, m, day] = d.split("-"); return `${day}/${m}`; }} tick={{ fontSize: 8, fill: "#adaaaa" }} tickLine={false} axisLine={false} interval={2} />
                        <YAxis tick={{ fontSize: 8, fill: "#adaaaa" }} tickLine={false} axisLine={false} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 10, borderRadius: 10, border: "none", background: "#262626", color: "#fff" }} formatter={v => [v, "Leads"]} labelFormatter={d => { const [, m, day] = d.split("-"); return `${day}/${m}`; }} />
                        <Bar dataKey="count" fill="#FFC107" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-36">
                      <p className="text-xs" style={{ color: "var(--ds-text-muted)" }}>Sin datos</p>
                    </div>
                  )}
                </SectionCard>

              </div>{/* /bottom 3-col */}
            </div>{/* /left col */}

            {/* ── RIGHT COLUMN: Actividad Reciente ────────────────────────── */}
            <div>
              <SectionCard title="Actividad Reciente" testId="activity-card">
                {activity.length === 0 ? (
                  <div className="space-y-4">
                    {[
                      { time: "Ahora", text: "Sistema listo y monitorizado", color: "#FFC107" },
                      { time: "Hoy",   text: "Dashboard actualizado",         color: "#3199BA" },
                    ].map((item) => (
                      <ActivityItem
                        key={item.time}
                        time={item.time}
                        title={item.text}
                        eventType=""
                        description=""
                        color={item.color}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="activity-card-feed">
                    {activity.slice(0, 8).map((item, i) => (
                      <ActivityItem
                        key={item.id || `${item.title || item.description}-${i}`}
                        time={item.ts || item.timestamp || item.time || item.created_at}
                        title={item.title || item.description || item.text}
                        eventType={item.event_type || item.type || ""}
                        description={item.description}
                        color={item.color || DONUT_COLORS[i % DONUT_COLORS.length]}
                      />
                    ))}
                  </div>
                )}

                <button
                  onClick={() => navigate("/activity-log")}
                  data-testid="view-full-activity-log-btn"
                  className="mt-5 w-full py-2 rounded-xl text-xs font-bold transition-all hover:opacity-90"
                  style={{ background: "rgba(255,193,7,0.10)", color: "var(--ds-primary)", border: "1px solid rgba(255,193,7,0.15)" }}
                >
                  Ver historial completo
                </button>
              </SectionCard>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────────────── */

function SectionCard({ title, children, testId, action }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl p-5 transition-colors"
      style={{ background: "var(--ds-surface-card)" }}
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: "var(--ds-text-muted)" }}>{title}</p>
        {action && (
          <button onClick={action.onClick} className="text-[10px] font-semibold transition-colors hover:opacity-80" style={{ color: "var(--ds-primary)" }}>
            {action.label}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function KpiPill({ testId, label, value, delta, positive }) {
  return (
    <div
      data-testid={testId}
      className="flex-shrink-0 rounded-xl px-4 py-2.5 flex items-center gap-3"
      style={{ background: "var(--ds-surface-low)", border: "1px solid rgba(255,255,255,0.06)" }}
    >
      <div>
        <p className="text-[10px] font-semibold whitespace-nowrap" style={{ color: "var(--ds-text-muted)" }}>{label}</p>
        <p className="text-lg font-black leading-none text-white" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{value}</p>
      </div>
      {delta && (
        <span
          className="text-[10px] font-bold flex items-center gap-0.5 ml-1"
          style={{ color: positive ? "#34D399" : "var(--ds-error)" }}
        >
          {positive ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
          {delta}
        </span>
      )}
    </div>
  );
}

// Iter93 — Etiquetas legibles de event_type (subset visible en el widget)
const TYPE_LABELS_HOME = {
  business_created:     "Lead",
  lead_received:        "Lead",
  research_done:        "Research",
  site_generated:       "Sitio",
  site_published:       "Sitio",
  sequence_created:     "Secuencia",
  sequence_approved:    "Secuencia",
  email_sent:           "Email",
  email_delivered:      "Email",
  email_opened:         "Email",
  email_bounced:        "Email",
  payment_received:     "Pago",
  income:               "Ingreso",
  expense:              "Egreso",
  ai_call:              "IA",
  client_login:         "Cliente",
  client_chat_accepted: "Cliente",
  client_site_resumed:  "Cliente",
  client_site_paused:   "Cliente",
};

function ActivityItem({ time, title, eventType, description, color }) {
  const formatTime = (t) => {
    if (!t) return "";
    try {
      const d = new Date(t);
      if (isNaN(d.getTime())) return t;
      const now = new Date();
      const diff = Math.floor((now - d) / 60000);
      if (diff < 1) return "Ahora";
      if (diff < 60) return `Hace ${diff} min`;
      if (diff < 60 * 24) return `Hace ${Math.floor(diff / 60)} h`;
      return d.toLocaleString("es-UY", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return t; }
  };
  const typeLabel = TYPE_LABELS_HOME[eventType] || (eventType ? eventType.replace(/_/g, " ") : "");
  return (
    <div className="flex gap-3" data-testid="activity-card-row">
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: color || "#FFC107" }} />
        <div className="w-px flex-1" style={{ background: "rgba(255,255,255,0.07)" }} />
      </div>
      <div className="pb-2 min-w-0 flex-1">
        {typeLabel && (
          <span
            className="inline-block px-1.5 py-0.5 rounded text-[8.5px] font-black uppercase tracking-wider mb-1"
            style={{ background: `${color}1f`, color, border: `1px solid ${color}33` }}
          >
            {typeLabel}
          </span>
        )}
        <p className="text-xs text-white leading-snug font-medium truncate">{title}</p>
        {description && description !== title && (
          <p className="text-[10.5px] text-gray-500 mt-0.5 leading-snug truncate">{description}</p>
        )}
        <div className="flex items-center gap-1 mt-1">
          <Clock size={9} style={{ color: "var(--ds-text-muted)" }} />
          <span style={{ fontSize: 9, color: "var(--ds-text-muted)" }}>{formatTime(time)}</span>
        </div>
      </div>
    </div>
  );
}

/* ── AutoRefreshBadge ────────────────────────────────────────────────────── */
/* Replaces the old "Sincronizar" button. Shows a subtle pulse + label that
 * the dashboard auto-updates every minute. No click required. */
function AutoRefreshBadge({ loading }) {
  return (
    <div
      data-testid="auto-refresh-badge"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold"
      style={{
        background: "rgba(255,255,255,0.03)",
        color: "var(--ds-text-muted)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
      title="Los datos se actualizan automáticamente cada minuto"
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{
          background: loading ? "#FFC107" : "#34D399",
          animation: loading ? "pulse 1s infinite" : "pulse 2.4s infinite",
        }}
      />
      {loading ? "Actualizando…" : "En vivo · cada 1m"}
    </div>
  );
}

